import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import carousel1 from '../../Assets/img/carousel/Carousel2.png';
import './ProductCatalog.scss';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { InteractionType } from '@azure/msal-browser';
import { CatalogDataService } from '../../Services/CatalogsDataService';
import noImage from '../../Assets/img/catalogs/no-product.png';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import DebouncedInput from '../../Components/Table/DebounceInput';
import { customStyles } from '../../Utils/reactSelectCustomStyles';

const ProductCatalog = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');

    // Pagination states:
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const pageCount = Math.ceil(totalCount / pageSize);

    // State for filter selections
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubCategories, setSelectedSubCategories] = useState([]);

    // State for available filter options (populated from API)
    const [brandOptions, setBrandOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);

    // Sorting states
    const [sortBy, setSortBy] = useState("name"); // options: name, price, size
    const [sortAsc, setSortAsc] = useState(true);

    const navigate = useNavigate();

    // Fetch filter options once the user is authenticated
    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchProductFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, instance, inProgress]);

    // Fetch the products whenever search, filters or sorting change
    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchProducts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, selectedBrands, selectedCategories, selectedSubCategories, sortBy, sortAsc, pageIndex, pageSize]);

    const fetchProductFilters = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const response = await CatalogDataService.getProductFilterOptions(tokenResponse.accessToken);
            if (response && response.data) {
                // Assuming the API returns { brands, categories, subcategories } as arrays of strings
                const { brands, categories, subCategories } = response.data;
                setBrandOptions(brands.map(item => ({ value: item, label: item })));
                setCategoryOptions(categories.map(item => ({ value: item, label: item })));
                setSubCategoryOptions(subCategories.map(item => ({ value: item, label: item })));
            }
        } catch (error) {
            console.error('Failed to fetch product filter options:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const params = {
                search,
                brands: selectedBrands.map(opt => opt.value),
                categories: selectedCategories.map(opt => opt.value),
                subcategories: selectedSubCategories.map(opt => opt.value),
                sortBy,
                sortAsc,
                pageIndex,
                pageSize
            };
            const response = await CatalogDataService.getProducts(params, tokenResponse.accessToken);
            if (response && response.data) {
                setProducts(response.data.items);
                setTotalCount(response.data.totalCount);
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
        }
    };

    return (
        <div className="product-catalog">
            {/* Header: Filters on the left, Search bar on the right */}
            <div className="catalog-header">
                <div className="filters">
                    <Select
                        isMulti
                        options={brandOptions}
                        placeholder="Filter by Brand"
                        value={selectedBrands}
                        onChange={selected => setSelectedBrands(selected || [])}
                        styles={customStyles}
                    />
                    <Select
                        isMulti
                        options={categoryOptions}
                        placeholder="Filter by Category"
                        value={selectedCategories}
                        onChange={selected => setSelectedCategories(selected || [])}
                        styles={customStyles}
                    />
                    <Select
                        isMulti
                        options={subCategoryOptions}
                        placeholder="Filter by Subcategory"
                        value={selectedSubCategories}
                        onChange={selected => setSelectedSubCategories(selected || [])}
                        styles={customStyles}
                    />
                </div>
                <div className="search-bar ms-4">
                    <FontAwesomeIcon icon={faSearch} />
                    {/* <input 
                        type="text" 
                        placeholder="search" 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    /> */}
                    <DebouncedInput type="text" placeholder="search" value={search} onChange={value => setSearch(value)} />
                </div>
            </div>

            {/* Products grid */}
            <div className="catalog-grid">
                {products.map((product) => (
                    <Card key={product.productSK} className="catalog-card" onClick={() => navigate(`/product-catalog/${product.productSK}`)}>
                        <Card.Img
                            variant="top"
                            src={product.blobImageURL || noImage}
                            onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = noImage;
                            }}
                        />
                        <Card.Body>
                            <Card.Title className="product-title">{product.productName}</Card.Title>
                            <Card.Subtitle className="product-subtitle">{product.size}</Card.Subtitle>
                            <div className="price-info">
                                <div className="price-label text-muted">Average Price</div>
                                <div className="price-value">
                                    <strong>{product.price ? `$${product.price.toFixed(2)}` : 'N/A'}</strong>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ))}
            </div>
            <div className="d-flex align-items-center gx-2 mt-3 pagination-controls">
                <nav>
                    <ul className="pagination m-0">
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="First" onClick={(e) => { e.preventDefault(); setPageIndex(0); }}>
                                <span aria-hidden="true">&laquo;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Previous" onClick={(e) => { e.preventDefault(); setPageIndex(pageIndex - 1); }}>
                                <span aria-hidden="true">&lt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Next" onClick={(e) => { e.preventDefault(); setPageIndex(pageIndex + 1); }}>
                                <span aria-hidden="true">&gt;</span>
                            </a>
                        </li>
                        <li className={`page-item ${pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
                            <a className="page-link" href="#" aria-label="Last" onClick={(e) => { e.preventDefault(); setPageIndex(pageCount - 1); }}>
                                <span aria-hidden="true">&raquo;</span>
                            </a>
                        </li>
                    </ul>
                </nav>
                <span className="d-flex align-items-center gx-1 ms-2">
                    <div>Page</div>
                    <strong>
                        {pageIndex + 1} of {pageCount}
                    </strong>
                </span>
                <span className="d-flex align-items-center gx-1 ms-2">
                    | Go to page:
                    <input
                        type="number"
                        defaultValue={pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            setPageIndex(page);
                        }}
                        className="form-control w-auto ms-1"
                    />
                </span>
                <select
                    value={pageSize}
                    onChange={e => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // reset to first page when page size changes
                    }}
                    className="form-select w-auto ms-2"
                >
                    {[10, 20, 30, 40, 50].map(size => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

const ProductCatalogPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Product Catalog">
                <ProductCatalog />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default ProductCatalogPage;
